import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, ToggleLeft, ToggleRight, Monitor, Smartphone, Globe, Megaphone, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';
import RedirectorMonetizacao from '../../components/system/RedirectorMonetizacao';
import { ORIGENS, LOJAS, errosAnuncio, corpoAnuncio } from '../../utils/redirectorAdmin';


const POSITIONS = [
    { key: 'home_top', label: 'Topo da Home' },
    { key: 'between_cards', label: 'Entre Cards' },
    { key: 'home_bottom', label: 'Fim da Listagem' },
    { key: 'sidebar', label: 'Sidebar Desktop' },
    { key: 'deal_page', label: 'Pagina da Oferta' },
    { key: 'redirect_interstitial', label: 'Pagina intermediaria (ir.supervitrineofertas.com)' },
];

const DEVICES = [
    { key: 'all', label: 'Todos', icon: Globe },
    { key: 'desktop', label: 'Desktop', icon: Monitor },
    { key: 'mobile', label: 'Mobile', icon: Smartphone },
];

export default function AdManagerPage() {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingAd, setEditingAd] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [aba, setAba] = useState('anuncios');
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchAds = () => {
        fetch(`${API}/ads/admin`, { headers })
            .then(r => r.json())
            .then(data => { if (Array.isArray(data)) setAds(data); })
            .catch(() => toast.error('Erro ao carregar anuncios'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchAds(); }, []);

    const emptyAd = { name: '', type: 'custom', status: 'inactive', position: 'home_top', device: 'all', priority: 0, start_date: '', end_date: '', google_slot: '', google_client: '', custom_image: '', custom_image_mobile: '', custom_title: '', custom_link: '', custom_cta: 'Ver oferta', redirect_kind: 'house', subtitle: '', advertiser: '', sources: [], marketplaces: [] };

    const openNew = () => { setEditingAd({ ...emptyAd }); setShowForm(true); };
    const openEdit = (ad) => { setEditingAd({ ...ad }); setShowForm(true); };
    const closeForm = () => { setEditingAd(null); setShowForm(false); };

    const handleSave = async () => {
        if (!editingAd.name) { toast.error('Nome obrigatorio'); return; }
        const erros = errosAnuncio(editingAd);
        if (erros.length) { toast.error(erros[0]); return; }
        const isNew = !editingAd.ad_id;
        const url = isNew ? `${API}/ads` : `${API}/ads/${editingAd.ad_id}`;
        const method = isNew ? 'POST' : 'PUT';
        try {
            const res = await fetch(url, { method, headers, body: JSON.stringify(corpoAnuncio(editingAd)) });
            if (res.ok) { toast.success(isNew ? 'Anuncio criado!' : 'Anuncio atualizado!'); closeForm(); fetchAds(); }
            else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
    };

    const handleDelete = async (adId) => {
        if (!window.confirm('Excluir este anuncio?')) return;
        try {
            await fetch(`${API}/ads/${adId}`, { method: 'DELETE', headers });
            toast.success('Anuncio excluido'); fetchAds();
        } catch { toast.error('Erro ao excluir'); }
    };

    const toggleStatus = async (ad) => {
        const newStatus = ad.status === 'active' ? 'inactive' : 'active';
        await fetch(`${API}/ads/${ad.ad_id}`, { method: 'PUT', headers, body: JSON.stringify({ status: newStatus }) });
        fetchAds();
    };

    const setField = (k, v) => setEditingAd(prev => ({ ...prev, [k]: v }));
    const alternaLista = (k, v) => setEditingAd(prev => {
        const atual = prev[k] || [];
        return { ...prev, [k]: atual.includes(v) ? atual.filter(x => x !== v) : [...atual, v] };
    });
    const intermediaria = editingAd && editingAd.position === 'redirect_interstitial';

    if (loading) return <div className="sv-page flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="sv-page" data-testid="ad-manager-page">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Megaphone size={20} style={{ color: 'var(--sv-gold-primary)' }} />
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Anuncios</h2>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--muted-2)' }}>{ads.length}</span>
                </div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={openNew} data-testid="new-ad-btn">
                    <Plus size={14} /> <span className="ml-1">Novo</span>
                </button>
            </div>

            <div className="flex gap-2 mb-4" role="tablist">
                <button role="tab" aria-selected={aba === 'anuncios'} className="sv-btn-system" onClick={() => setAba('anuncios')} data-testid="tab-anuncios">Anuncios</button>
                <button role="tab" aria-selected={aba === 'redirector'} className="sv-btn-system" onClick={() => setAba('redirector')} data-testid="tab-redirector">Redirector / Monetizacao</button>
            </div>
            {aba === 'redirector' && <RedirectorMonetizacao />}
            {aba === 'anuncios' && (<>

            {/* Ad Form Modal */}
            {showForm && editingAd && (
                <div className="sv-card-system mb-6" style={{ border: '1px solid var(--sv-gold-primary)' }} data-testid="ad-form">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>{editingAd.ad_id ? 'Editar' : 'Novo'} Anuncio</span>
                        <button onClick={closeForm} className="sv-btn-system"><X size={14} /></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        <Field label="Nome" value={editingAd.name} onChange={v => setField('name', v)} testid="ad-name" />
                        <div>
                            <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--muted-2)' }}>Tipo</label>
                            <select value={editingAd.type} onChange={e => setField('type', e.target.value)} className="w-full px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ad-type">
                                <option value="custom">Personalizado</option>
                                <option value="google">Google Ads</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--muted-2)' }}>Posição</label>
                            <select value={editingAd.position} onChange={e => setField('position', e.target.value)} className="w-full px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ad-position">
                                {POSITIONS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--muted-2)' }}>Dispositivo</label>
                            <select value={editingAd.device} onChange={e => setField('device', e.target.value)} className="w-full px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ad-device">
                                {DEVICES.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                            </select>
                        </div>
                        <Field label="Prioridade" value={editingAd.priority} onChange={v => setField('priority', parseInt(v) || 0)} type="number" testid="ad-priority" />
                        <Field label="Data inicio" value={editingAd.start_date} onChange={v => setField('start_date', v)} type="date" testid="ad-start" />
                        <Field label="Data fim" value={editingAd.end_date} onChange={v => setField('end_date', v)} type="date" testid="ad-end" />
                    </div>

                    {editingAd.type === 'google' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                            <Field label="Google Client ID" value={editingAd.google_client} onChange={v => setField('google_client', v)} testid="ad-gclient" />
                            <Field label="Google Slot ID" value={editingAd.google_slot} onChange={v => setField('google_slot', v)} testid="ad-gslot" />
                        </div>
                    )}

                    {editingAd.type === 'custom' && (
                        <div className="space-y-3 mb-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Titulo" value={editingAd.custom_title} onChange={v => setField('custom_title', v)} testid="ad-title" />
                                <Field label="Link destino" value={editingAd.custom_link} onChange={v => setField('custom_link', v)} testid="ad-link" />
                                <Field label="CTA (botao)" value={editingAd.custom_cta} onChange={v => setField('custom_cta', v)} testid="ad-cta" />
                                <Field label="Banner Desktop (URL)" value={editingAd.custom_image} onChange={v => setField('custom_image', v)} testid="ad-img" />
                                <Field label="Banner Mobile (URL)" value={editingAd.custom_image_mobile} onChange={v => setField('custom_image_mobile', v)} testid="ad-img-mobile" />
                            </div>
                            {intermediaria && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3" style={{ borderTop: '1px solid var(--border)' }} data-testid="ad-redirect-fields">
                                    <div>
                                        <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--muted-2)' }}>Tipo (pagina intermediaria)</label>
                                        <select value={editingAd.redirect_kind || 'house'} onChange={e => setField('redirect_kind', e.target.value)} className="w-full px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ad-redirect-kind">
                                            <option value="house">Banner Super Vitrine (so links do Super Vitrine)</option>
                                            <option value="sponsor">Patrocinado (https do anunciante)</option>
                                        </select>
                                    </div>
                                    <Field label="Subtitulo" value={editingAd.subtitle} onChange={v => setField('subtitle', v)} testid="ad-subtitle" />
                                    <Field label="Anunciante" value={editingAd.advertiser} onChange={v => setField('advertiser', v)} testid="ad-advertiser" />
                                    <div>
                                        <span className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Origens (vazio = todas)</span>
                                        {ORIGENS.map(o => (
                                            <label key={o.key} className="inline-flex items-center gap-1 text-xs mr-3" style={{ color: 'var(--text)' }}>
                                                <input type="checkbox" checked={(editingAd.sources || []).includes(o.key)} onChange={() => alternaLista('sources', o.key)} /> {o.label}
                                            </label>
                                        ))}
                                    </div>
                                    <div>
                                        <span className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Lojas (vazio = todas)</span>
                                        {LOJAS.map(l => (
                                            <label key={l.key} className="inline-flex items-center gap-1 text-xs mr-3" style={{ color: 'var(--text)' }}>
                                                <input type="checkbox" checked={(editingAd.marketplaces || []).includes(l.key)} onChange={() => alternaLista('marketplaces', l.key)} /> {l.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <button className="sv-btn-gold-system sv-btn-system w-full justify-center" onClick={handleSave} data-testid="ad-save-btn">
                        <Save size={14} /> <span className="ml-1">Salvar Anuncio</span>
                    </button>
                </div>
            )}

            {/* Ads List */}
            {ads.length === 0 ? (
                <div className="text-center py-12 sv-card-system">
                    <Megaphone size={32} style={{ color: 'var(--muted-2)', margin: '0 auto 12px' }} />
                    <p style={{ color: 'var(--muted)' }}>Nenhum anuncio cadastrado</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted-2)' }}>Crie seu primeiro anuncio para monetizar o site</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {ads.map(ad => (
                        <div key={ad.ad_id} className="sv-card-system" style={{ padding: '12px 14px' }} data-testid={`ad-${ad.ad_id}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <button onClick={() => toggleStatus(ad)} data-testid={`toggle-ad-${ad.ad_id}`}>
                                        {ad.status === 'active' ? <ToggleRight size={28} style={{ color: '#22c55e' }} /> : <ToggleLeft size={28} style={{ color: 'var(--muted-2)' }} />}
                                    </button>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{ad.name}</p>
                                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                            <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: ad.type === 'google' ? 'rgba(66,133,244,0.12)' : 'rgba(var(--sv-accent-rgb), 0.12)', color: ad.type === 'google' ? '#4285F4' : 'var(--sv-accent)' }}>{ad.type === 'google' ? 'Google' : 'Custom'}</span>
                                            <span className="text-[9px]" style={{ color: 'var(--muted-2)' }}>{POSITIONS.find(p => p.key === ad.position)?.label || ad.position}</span>
                                            <span className="text-[9px]" style={{ color: 'var(--muted-2)' }}>{ad.device}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                    <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => openEdit(ad)} data-testid={`edit-ad-${ad.ad_id}`}><Edit size={12} /></button>
                                    <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => handleDelete(ad.ad_id)} data-testid={`delete-ad-${ad.ad_id}`}><Trash2 size={12} style={{ color: '#ef4444' }} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            </>)}
        </div>
    );
}

function Field({ label, value, onChange, type = 'text', testid }) {
    return (
        <div>
            <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--muted-2)' }}>{label}</label>
            <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid={testid} />
        </div>
    );
}
